// frontend/src/pages/ConfigureConnector.jsx
//
// Per-connector settings page. Reached from the Connectors list "Configure"
// menu item. Lets the user:
//   - Rename the connector (display_name)
//   - Set the sync frequency (gated by plan tier)
//   - Pause / resume syncing (config_json.enabled)
// And shows read-only metadata: connector type, creator, created date, ID.
//
// Plan tier source: GET /organization returns the org's plan. We use that
// to filter the frequency dropdown via allowedFrequenciesFor().

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Save,
  Pause,
  Play,
  Loader2,
  AlertCircle,
  CheckCircle,
  Lock,
  Calendar,
  User,
  Hash,
  Settings,
  RefreshCw,
  Filter,
  Activity,
  AlertTriangle,
  Clock,
  XCircle,
  Check,
  ChevronDown,
} from 'lucide-react';
import { Input, Label } from '@/components/ui/input.jsx';
import { Button } from '@/components/ui/button.jsx';
import { apiCall } from '../utils/api';
import { canEditConnector, canSyncConnector } from '../utils/roles';
import {
  FREQUENCY_ORDER,
  frequencyLabel,
  allowedFrequenciesFor,
  PLAN_FOR_FREQUENCY,
} from '../constants/frequencies';


export default function ConfigureConnector() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [savedToast, setSavedToast] = useState(null);

  // Source-of-truth state from server
  const [config, setConfig] = useState(null);
  const [orgPlan, setOrgPlan] = useState('trial');
  const [creator, setCreator] = useState(null);

  // Edit state (mirrors config; PATCHed on save)
  const [displayName, setDisplayName] = useState('');
  const [syncFrequency, setSyncFrequency] = useState(null);
  const [enabled, setEnabled] = useState(true);

  // ─────────────────────────────────────────────────────────────
  // Load
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const [cfgRes, orgRes] = await Promise.all([
          apiCall(`/sync/configs/${id}`),
          apiCall('/organization/'),
        ]);

        if (!cfgRes.ok) {
          if (cfgRes.status === 404) throw new Error('Connector not found');
          throw new Error(`Failed to load connector (HTTP ${cfgRes.status})`);
        }
        const cfg = await cfgRes.json();
        if (cancelled) return;

        setConfig(cfg);
        setDisplayName(cfg.display_name || '');
        setSyncFrequency(cfg.sync_frequency || null);
        setEnabled(cfg.config_json?.enabled !== false);

        if (orgRes.ok) {
          const org = await orgRes.json();
          setOrgPlan((org.plan || 'trial').toLowerCase());
        }

        if (cfg.created_by_user_id) {
          // Best-effort: not fatal if it fails
          const userRes = await apiCall(`/users/${cfg.created_by_user_id}`);
          if (userRes.ok) {
            const u = await userRes.json();
            if (!cancelled) setCreator(u);
          }
        }
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [id]);

  


  // ─────────────────────────────────────────────────────────────
  // Sync history + Sync now
  // ─────────────────────────────────────────────────────────────
  const [syncRuns, setSyncRuns] = useState([]);
  const [syncRunsLoading, setSyncRunsLoading] = useState(true);
  const [syncTriggering, setSyncTriggering] = useState(false);
  const [syncFlash, setSyncFlash] = useState(null);
  // Entity multi-select for "sync now"
  const [availableEntities, setAvailableEntities] = useState([]);
  const [selectedEntities, setSelectedEntities] = useState(new Set());
  const [showEntityPicker, setShowEntityPicker] = useState(false);
  const [filterRunType, setFilterRunType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  // Load the connector's supported entities for the sync picker.
  // Defaults all selected (matches "sync everything" behavior).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiCall(`/sync/configs/${id}/entities`);
        if (!res.ok) return;
        const body = await res.json();
        const ents = Array.isArray(body.entities) ? body.entities : [];
        if (cancelled) return;
        setAvailableEntities(ents);
        setSelectedEntities(new Set(ents)); // default: all
      } catch {
        /* non-fatal: picker just won't show */
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  async function fetchRuns() {
    try {
      const params = new URLSearchParams({ limit: '20' });
      if (filterRunType !== 'all') params.set('run_type', filterRunType);
      if (filterStatus !== 'all') params.set('status', filterStatus);
      const res = await apiCall(`/sync/configs/${id}/runs?${params.toString()}`);
      if (!res.ok) {
        if (res.status === 404) {
          // Config doesn't exist (e.g., deleted in another tab) -- bail out
          return;
        }
        console.warn('Failed to load sync runs:', res.status);
        return;
      }
      const body = await res.json();
      setSyncRuns(body.runs || []);
    } catch (err) {
      console.warn('Failed to load sync runs:', err);
    } finally {
      setSyncRunsLoading(false);
    }
  }

  // Initial fetch + poll while any run is in 'running' state
  useEffect(() => {
    let cancelled = false;
    let timer = null;

    async function tick() {
      if (cancelled) return;
      await fetchRuns();
      // After fetch, decide whether to schedule another poll
      const stillRunning = syncRuns.some((r) => r.status === 'running');
      if (!cancelled && (stillRunning || syncTriggering)) {
        timer = setTimeout(tick, 3000);
      } else if (!cancelled) {
        // Slow heartbeat so a manual trigger from another tab still surfaces
        timer = setTimeout(tick, 30000);
      }
    }

    tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, syncTriggering, filterRunType, filterStatus]);

  async function handleSyncNow() {
    setSyncTriggering(true);
    setSyncFlash(null);
    try {
      // If the user has narrowed the selection, send it. If everything
      // is selected (or the picker never loaded), send no entities so
      // the backend syncs all (original behavior).
      const allSelected =
        availableEntities.length > 0 &&
        selectedEntities.size === availableEntities.length;
      const bodyObj =
        allSelected || selectedEntities.size === 0
          ? {}
          : { entities: Array.from(selectedEntities) };
      const res = await apiCall(`/sync/configs/${id}/run-now`, {
        method: 'POST',
        body: JSON.stringify(bodyObj),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `Sync failed (HTTP ${res.status})`);
      }
      const body = await res.json();
      setSyncFlash({ type: 'ok', message: `Sync started for ${(body.entities || []).length} entities` });
      // Force a fresh fetch immediately so the user sees 'running' rows
      await fetchRuns();
    } catch (err) {
      setSyncFlash({ type: 'error', message: err.message || 'Sync failed' });
    } finally {
      setSyncTriggering(false);
      setTimeout(() => setSyncFlash(null), 4000);
    }
  }


  // ─────────────────────────────────────────────────────────────
  // Save
  // ─────────────────────────────────────────────────────────────
  async function handleSave() {
    if (!config) return;
    setSaving(true);
    setError(null);
    setSavedToast(null);

    // Build minimal patch with only changed fields
    const body = {};
    if (displayName !== config.display_name) {
      body.display_name = displayName;
    }
    if (syncFrequency !== config.sync_frequency) {
      body.sync_frequency = syncFrequency;
    }
    const wasEnabled = config.config_json?.enabled !== false;
    if (enabled !== wasEnabled) {
      body.enabled = enabled;
    }

    if (Object.keys(body).length === 0) {
      setSavedToast('No changes to save');
      setSaving(false);
      return;
    }

    try {
      const res = await apiCall(`/sync/configs/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        // Special-case the 402 frequency-not-allowed error
        if (res.status === 402 && data?.detail?.code === 'frequency_not_allowed') {
          throw new Error(
            data.detail.message ||
              `That frequency requires a higher plan tier.`
          );
        }
        throw new Error(data?.detail || `Save failed (HTTP ${res.status})`);
      }
      const updated = await res.json();
      setConfig(updated);
      setSavedToast('Saved');
      // Brief flash, then clear
      setTimeout(() => setSavedToast(null), 2500);
    } catch (e) {
      setError(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }


  // ─────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0a]">
        <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
      </div>
    );
  }

  if (error && !config) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-[#0a0a0a]">
        <AlertCircle className="w-8 h-8 text-red-400" />
        <p className="text-red-400">{error}</p>
        <Link to="/connectors" className="text-blue-400 hover:underline">
          Back to connectors
        </Link>
      </div>
    );
  }

  const allowed = new Set(allowedFrequenciesFor(orgPlan));

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <div className="max-w-3xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/connectors')}
            className="flex items-center gap-2 text-sm text-[#a0a0a0] hover:text-white mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to connectors
          </button>
          <div className="flex items-center justify-between gap-4">
            <h1 className="text-2xl font-semibold flex items-center gap-3">
              <Settings className="w-6 h-6 text-blue-500" />
              Configure connector
            </h1>
            {canSyncConnector() && (
            <Button
              variant="outline"
              onClick={handleSyncNow}
              disabled={syncTriggering}
            >
              {syncTriggering ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Starting...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4" />
                  Sync now
                </>
              )}
            </Button>
            )}
          </div>
          {syncFlash && (
            <div
              className={`mt-3 text-sm flex items-center gap-2 ${
                syncFlash.type === "ok" ? "text-green-400" : "text-red-400"
              }`}
            >
              {syncFlash.type === "ok" ? (
                <CheckCircle className="w-4 h-4" />
              ) : (
                <AlertCircle className="w-4 h-4" />
              )}
              {syncFlash.message}
            </div>
          )}

          {canSyncConnector() && availableEntities.length > 0 && (
            <EntitySyncPicker
              entities={availableEntities}
              selected={selectedEntities}
              setSelected={setSelectedEntities}
              open={showEntityPicker}
              setOpen={setShowEntityPicker}
            />
          )}
        </div>

        {/* General section */}
        <Section title="General">
          <Field label="Display name">
            <Input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Connector display name"
            />
            <p className="text-xs text-[#6a6a6a] mt-1">
              Customers and teammates see this name. You can rename anytime.
            </p>
          </Field>
        </Section>

        {/* Schedule section */}
        <Section title="Sync schedule">
          <Field label="Sync frequency">
            <select
              className="w-full bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
              value={syncFrequency || ''}
              onChange={(e) =>
                setSyncFrequency(e.target.value === '' ? null : e.target.value)
              }
            >
              <option value="">Manual only (don't auto-sync)</option>
              {FREQUENCY_ORDER.map((token) => {
                const isAllowed = allowed.has(token);
                const upgradeTo = PLAN_FOR_FREQUENCY[token] || '';
                const label = isAllowed
                  ? frequencyLabel(token)
                  : `${frequencyLabel(token)} — requires ${upgradeTo} plan`;
                return (
                  <option key={token} value={token} disabled={!isAllowed}>
                    {label}
                  </option>
                );
              })}
            </select>
            <p className="text-xs text-[#6a6a6a] mt-1">
              Connex polls your data source on this cadence. Manual means
              no scheduled sync; you can still trigger one anytime.
            </p>
          </Field>
        </Section>

        {/* Status section */}
        <Section title="Status">
          <div className="flex items-center justify-between p-4 rounded-lg border border-[#2a2a2a] bg-[#1a1a1a]">
            <div className="flex items-center gap-3">
              {enabled ? (
                <Play className="w-5 h-5 text-green-400" />
              ) : (
                <Pause className="w-5 h-5 text-yellow-400" />
              )}
              <div>
                <p className="text-sm font-medium">
                  {enabled ? 'Active' : 'Paused'}
                </p>
                <p className="text-xs text-[#6a6a6a]">
                  {enabled
                    ? 'Scheduled syncs run on the cadence above.'
                    : 'Scheduled syncs are paused. Manual syncs still work.'}
                </p>
              </div>
            </div>
            <button
              onClick={() => setEnabled(!enabled)}
              className="text-sm text-blue-400 hover:text-blue-300"
            >
              {enabled ? 'Pause' : 'Resume'}
            </button>
          </div>
        </Section>

        {/* Sync history section with collapsible filters */}
        <section className="mb-8">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm uppercase tracking-wide text-[#6a6a6a]">
              Sync history
            </h2>
            <button
              onClick={() => setShowFilters((s) => !s)}
              className="flex items-center gap-1.5 text-xs text-[#a0a0a0] hover:text-white"
            >
              <Filter className="w-3.5 h-3.5" />
              Filter
              {(filterRunType !== 'all' || filterStatus !== 'all') && (
                <span className="ml-1 px-1.5 py-0.5 text-[10px] bg-blue-500 text-white rounded-full">
                  {[
                    filterRunType !== 'all' ? 1 : 0,
                    filterStatus !== 'all' ? 1 : 0,
                  ].reduce((a, b) => a + b, 0)}
                </span>
              )}
            </button>
          </div>

          {showFilters && (
            <div className="mb-3 p-3 bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <Label className="text-xs text-[#a0a0a0]">Type</Label>
                <select
                  value={filterRunType}
                  onChange={(e) => setFilterRunType(e.target.value)}
                  className="bg-[#0f0f0f] border border-[#2a2a2a] rounded px-2 py-1 text-xs text-white focus:outline-none focus:border-blue-500"
                >
                  <option value="all">All</option>
                  <option value="manual">Manual</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="wizard">Wizard</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-xs text-[#a0a0a0]">Status</Label>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="bg-[#0f0f0f] border border-[#2a2a2a] rounded px-2 py-1 text-xs text-white focus:outline-none focus:border-blue-500"
                >
                  <option value="all">All</option>
                  <option value="success">Success</option>
                  <option value="error">Error</option>
                  <option value="running">Running</option>
                </select>
              </div>
              {(filterRunType !== 'all' || filterStatus !== 'all') && (
                <button
                  onClick={() => {
                    setFilterRunType('all');
                    setFilterStatus('all');
                  }}
                  className="text-xs text-blue-400 hover:text-blue-300 ml-auto"
                >
                  Clear filters
                </button>
              )}
            </div>
          )}

          <div className="space-y-4">
            <SyncHistoryList
              runs={syncRuns}
              loading={syncRunsLoading}
            />
          </div>
        </section>

        <Section title="Details">
          <MetaRow
            icon={<Hash className="w-4 h-4" />}
            label="Connector ID"
            value={`#${config.id}`}
          />
          <MetaRow
            icon={<User className="w-4 h-4" />}
            label="Created by"
            value={
              creator?.email ||
              (config.created_by_user_id
                ? `User #${config.created_by_user_id}`
                : 'Unknown')
            }
          />
          <MetaRow
            icon={<Calendar className="w-4 h-4" />}
            label="Created at"
            value={
              config.created_at
                ? new Date(config.created_at).toLocaleString()
                : '—'
            }
          />
          <MetaRow
            icon={<Calendar className="w-4 h-4" />}
            label="Last synced at"
            value={
              config.last_synced_at
                ? new Date(config.last_synced_at).toLocaleString()
                : 'Never'
            }
          />
        </Section>

        {/* Footer: save button + status */}
        <div className="mt-8 pt-6 border-t border-[#2a2a2a] flex items-center justify-between">
          <div className="flex items-center gap-3">
            {savedToast && (
              <span className="flex items-center gap-2 text-sm text-green-400">
                <CheckCircle className="w-4 h-4" />
                {savedToast}
              </span>
            )}
            {error && config && (
              <span className="flex items-center gap-2 text-sm text-red-400">
                <AlertCircle className="w-4 h-4" />
                {error}
              </span>
            )}
          </div>
          {canEditConnector() && (
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save changes
              </>
            )}
          </Button>
          )}
        </div>
      </div>
    </div>
  );
}


// ============================================================
// Layout helpers
// ============================================================

// ============================================================
// Entity sync picker (multi-select)
// ============================================================

function EntitySyncPicker({ entities, selected, setSelected, open, setOpen }) {
  const allSelected = selected.size === entities.length;
  const noneSelected = selected.size === 0;

  const toggle = (ent) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(ent)) next.delete(ent);
      else next.add(ent);
      return next;
    });
  };

  const summary = allSelected
    ? 'All entities'
    : noneSelected
    ? 'No entities selected'
    : `${selected.size} of ${entities.length} entities`;

  return (
    <div className="mt-3">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 text-xs text-[#a0a0a0] hover:text-white"
      >
        <ChevronDown
          className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`}
        />
        Sync scope: <span className="text-white">{summary}</span>
      </button>

      {open && (
        <div className="mt-2 p-3 bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg">
          <div className="flex items-center gap-3 mb-2 pb-2 border-b border-[#2a2a2a]">
            <button
              type="button"
              onClick={() => setSelected(new Set(entities))}
              className="text-xs text-blue-400 hover:text-blue-300"
            >
              Select all
            </button>
            <button
              type="button"
              onClick={() => setSelected(new Set())}
              className="text-xs text-blue-400 hover:text-blue-300"
            >
              Select none
            </button>
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 max-h-56 overflow-y-auto">
            {entities.map((ent) => {
              const checked = selected.has(ent);
              return (
                <button
                  key={ent}
                  type="button"
                  onClick={() => toggle(ent)}
                  className="flex items-center gap-2 py-1 text-left text-sm text-[#e0e0e0] hover:text-white"
                >
                  <span
                    className={`flex items-center justify-center w-4 h-4 rounded border flex-shrink-0 ${
                      checked
                        ? 'bg-blue-500 border-blue-500'
                        : 'border-[#3a3a3a] bg-transparent'
                    }`}
                  >
                    {checked && <Check className="w-3 h-3 text-white" />}
                  </span>
                  <span className="truncate">{ent.replace(/_/g, ' ')}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-8">
      <h2 className="text-sm uppercase tracking-wide text-[#6a6a6a] mb-3">
        {title}
      </h2>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      {children}
    </div>
  );
}

function MetaRow({ icon, label, value }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-[#2a2a2a] last:border-b-0">
      <div className="flex items-center gap-2 text-sm text-[#a0a0a0]">
        {icon}
        <span>{label}</span>
      </div>
      <span className="text-sm text-white">{value}</span>
    </div>
  );
}

// ============================================================
// Sync history list
// ============================================================

function SyncHistoryList({ runs, loading }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
      </div>
    );
  }
  if (!runs || runs.length === 0) {
    return (
      <div className="py-6 text-center text-sm text-[#6a6a6a]">
        No syncs yet. Click "Sync now" to trigger one.
      </div>
    );
  }
  return (
    <div className="border border-[#2a2a2a] rounded-lg overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-[#1a1a1a] text-[#a0a0a0]">
          <tr>
            <th className="text-left px-3 py-2 font-normal">When</th>
            <th className="text-left px-3 py-2 font-normal">Type</th>
            <th className="text-left px-3 py-2 font-normal">Entity</th>
            <th className="text-left px-3 py-2 font-normal">Status</th>
            <th className="text-right px-3 py-2 font-normal">Duration</th>
            <th className="text-right px-3 py-2 font-normal">Rows</th>
          </tr>
        </thead>
        <tbody>
          {runs.map((r) => (
            <SyncHistoryRow key={r.id} run={r} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SyncHistoryRow({ run }) {
  const when = run.started_at ? new Date(run.started_at) : null;
  const whenStr = when ? when.toLocaleString() : '\u2014';
  const duration =
    run.duration_seconds != null ? `${run.duration_seconds.toFixed(1)}s` : '\u2014';
  return (
    <tr className="border-t border-[#2a2a2a] hover:bg-[#0f0f0f]">
      <td className="px-3 py-2 text-[#a0a0a0]">{whenStr}</td>
      <td className="px-3 py-2 text-[#a0a0a0] capitalize">{run.run_type || '\u2014'}</td>
      <td className="px-3 py-2 text-white">{run.entity || '\u2014'}</td>
      <td className="px-3 py-2">
        <SyncStatusBadge status={run.status} />
        {run.status === 'error' && run.error_message && (
          <div className="text-xs text-red-400 mt-1 max-w-md truncate" title={run.error_message}>
            {run.error_message}
          </div>
        )}
      </td>
      <td className="px-3 py-2 text-right text-[#a0a0a0]">{duration}</td>
      <td className="px-3 py-2 text-right text-white">{run.rows_inserted ?? 0}</td>
    </tr>
  );
}

function SyncStatusBadge({ status }) {
  const config = {
    success: { color: 'text-green-400', icon: CheckCircle, label: 'Success' },
    running: { color: 'text-blue-400', icon: Loader2, label: 'Running', spin: true },
    error: { color: 'text-red-400', icon: XCircle, label: 'Error' },
  }[status] || { color: 'text-[#6a6a6a]', icon: AlertTriangle, label: status || 'Unknown' };

  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs ${config.color}`}>
      <Icon className={`w-3.5 h-3.5 ${config.spin ? 'animate-spin' : ''}`} />
      {config.label}
    </span>
  );
}
